import { useQueries, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { attachSolutionFiles, linkSolutionToTicket } from '../api/easeedesk'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import {
  levelDescriptionQuery,
  solutionEntriesQuery,
  solutionsPageQuery,
  type SolutionCategory,
  type SolutionEntry,
} from '../api/easeedeskSolutions'
import { Spinner } from '../ui/Spinner'
import { SolutionFeedback } from './SolutionFeedback'
import { TicketSolutionRunbooks } from './TicketSolutionRunbooks'
import './TicketSolutions.css'

/**
 * Fila "IT Solutions" a fisei de tichet - `Easeedesk::ticketSolutions()` cu
 * macheta `easeedesk/tickets/solutions/index.php`.
 *
 * ---------------------------------------------------------------------------
 * ACELEASI SOLUTII, NU O A DOUA COPIE A LOR
 * ---------------------------------------------------------------------------
 *
 * Fila si ecranul mare "IT Solutions" arata acelasi lucru: biblioteca de solutii
 * a clientului, pe categorii, fiecare solutie cu cele patru trepte, tehnologia
 * ei si fisierele agatate. In portal erau doua transcrieri, si controllerul
 * filei si-o scrisese pe a lui de mana, cu patru interogari brute
 * (`it_solutions_subcategory`, `it_solutions_level`, `it_solutions_attachments`)
 * facute langa modelele care raspund la aceleasi intrebari.
 *
 * Aici fila cere de la ACELEASI capete ca ecranul mare - si sub acelasi drept,
 * `easeedesk.it_solutions.read`, care e chiar dreptul filei din portal.
 *
 * ---------------------------------------------------------------------------
 * CUM ARATA: CARDURI, CA IN PORTAL
 * ---------------------------------------------------------------------------
 *
 * Macheta portalului nu e un tabel, ci o lista de carduri: bec, numele
 * solutiei, tehnologia ca eticheta, numarul de fisiere, si un caret care
 * desface cardul. Inauntru: fisierele ca etichete rotunjite, apoi treptele,
 * fiecare cu bulina ei colorata (rosu, portocaliu, galben, verde), iar la
 * capat tehnologia si descrierea "specifice" clientului.
 *
 * Fila sta in jumatate de ecran, in timp ce se lucreaza la altceva, deci n-are
 * butoane de scriere: biblioteca se scrie pe ecranul ei.
 *
 * Lista unei categorii se cere abia cand categoria e deschisa, iar textul
 * treptelor abia cand se desface cardul - ca in portal, unde notele fiecarei
 * solutii vin tot la deschidere.
 */

export function TicketSolutions({ id }: { id: number }) {
  const { data, error } = useQuery(solutionsPageQuery)

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.categories.length === 0) {
    return (
      <div className="tsol ticket-solutions" id={`ts-${id}`}>
        {/* Runbook-ul aplicatiei tichetului, cu bife - inaintea bibliotecii. */}
        <TicketSolutionRunbooks id={id} />
        <div className="tsol-empty">
          <i className="fas fa-lightbulb tsol-empty-bulb" aria-hidden="true"></i>
          <div className="tsol-empty-text">
            No solutions have been written for this customer yet.
            <br />
            <a href="/app/easeedesk/it_solutions" target="_blank" rel="noopener noreferrer">
              Add them under Easeedesk &rsaquo; IT Solutions
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tsol ticket-solutions ts-wrap" id={`ts-${id}`}>
      <TicketSolutionRunbooks id={id} />
      {data.categories.map((category) => (
        <Category key={category.id} category={category} ticket={id} />
      ))}
    </div>
  )
}

const PAGE = 10

function Category({ category, ticket }: { category: SolutionCategory; ticket: number }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  /*
   * Lista se cere abia cand categoria e deschisa. Un client cu douasprezece
   * categorii ar fi facut douasprezece cereri la deschiderea filei, pentru
   * unsprezece liste pe care nu le citeste nimeni.
   */
  const list = useQuery({ ...solutionEntriesQuery(category.id, search, start, PAGE, 'asc'), enabled: open })

  const rows = list.data?.data ?? []
  const total = list.data?.recordsTotal ?? 0

  return (
    <section className={`ts-cat${open ? ' open' : ''}`}>
      <div className="tsol-cat ts-cat-title" onClick={() => setOpen(!open)}>
        <i className="fas fa-chevron-right tsol-cat-caret" aria-hidden="true"></i>
        {category.name}
      </div>

      {open && (
        <>
          <div className="tsol-bar">
            <div className="tsol-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="text"
                autoComplete="off"
                placeholder="Search solutions…"
                value={search}
                onChange={(ev) => {
                  setSearch(ev.target.value)
                  setStart(0)
                }}
              />
              {search !== '' && (
                <button
                  type="button"
                  className="tsol-clear"
                  title="Clear"
                  onClick={() => {
                    setSearch('')
                    setStart(0)
                  }}
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>

          {rows.length === 0 ? (
            list.isPending ? (
              <Spinner />
            ) : (
              <div className="tsol-none">
                {search !== '' ? 'Nothing matches that.' : 'Nothing written under this heading yet.'}
              </div>
            )
          ) : (
            rows.map((row) => <Card key={row.ID} row={row} ticket={ticket} />)
          )}

          {total > PAGE && (
            <div className="tsol-pager">
              <button
                type="button"
                className="btn btn-default btn-xs"
                disabled={start <= 0}
                onClick={() => setStart(Math.max(0, start - PAGE))}
              >
                Previous
              </button>{' '}
              <span className="tsol-pager-text">
                {start + 1} - {Math.min(start + PAGE, total)} of {total}
              </span>{' '}
              <button
                type="button"
                className="btn btn-default btn-xs"
                disabled={start + PAGE >= total}
                onClick={() => setStart(start + PAGE)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

/* Culorile treptelor, ca in portal (`$levelColor` din macheta). */
const LEVELS = ['0', '1', '2', '3']
const LEVEL_COLORS: Record<string, string> = { '0': '#d83430', '1': '#ffa500', '2': '#e0b400', '3': '#359c45' }

function Card({ row, ticket }: { row: SolutionEntry; ticket: number }) {
  const [open, setOpen] = useState(false)
  /* Bifele "Choose for the ticket" (`.tsol-file-pick`) si butonul
     "Attach to ticket" (`.tsol-attach`), ca in macheta: butonul apare cand e
     bifat macar un fisier, si bifele se sterg dupa ce fisierele au plecat -
     lasate, al doilea clic le-ar fi pus inca o data. */
  const [picked, setPicked] = useState<number[]>([])
  const [attaching, setAttaching] = useState(false)
  const attach = () => {
    if (picked.length === 0 || attaching) return
    setAttaching(true)
    attachSolutionFiles(ticket, picked)
      .then((answer) => {
        if (answer.status !== 'OK') {
          toast(answer.message || 'Nothing was attached', 'Error', 'error')
          return
        }
        toast(answer.message, 'Attached', 'success')
        setPicked([])
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Nothing was attached', 'Error', 'error'))
      .finally(() => setAttaching(false))
  }
  /* Legatura articol - tichet fara fisiere: un articol fara atasamente nu
     avea pana acum niciun drum spre tichet. */
  const [linking, setLinking] = useState(false)
  const [linked, setLinked] = useState(false)
  const link = () => {
    if (linking) return
    setLinking(true)
    linkSolutionToTicket(ticket, row.ID)
      .then((answer) => {
        if (answer.status !== 'OK') {
          toast(answer.message || 'The article was not linked', 'Error', 'error')
          return
        }
        toast(answer.message, 'Linked', 'success')
        setLinked(true)
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'The article was not linked', 'Error', 'error'))
      .finally(() => setLinking(false))
  }
  const tech = row.technology.trim()
  const desc = row.description.trim()
  const files = row.attachments.length

  return (
    <div className={`tsol-card${open ? ' open' : ''}`}>
      <div className="tsol-head" onClick={() => setOpen(!open)}>
        <i className="fas fa-lightbulb tsol-bulb" aria-hidden="true"></i>
        <span className="tsol-title">
          <span className="tsol-line">
            <span className="tsol-name">{row.subcategory_name}</span>
            {tech !== '' && <span className="tsol-tech">{tech}</span>}
            {files > 0 && (
              <span className="tsol-clip" title={`${files} attachment${files === 1 ? '' : 's'}`}>
                <i className="fas fa-paperclip" aria-hidden="true"></i> {files}
              </span>
            )}
          </span>
        </span>
        <i className="fas fa-chevron-right tsol-caret" aria-hidden="true"></i>
      </div>

      {open && (
        <div className="tsol-body">
          <div className="tsol-link-row">
            <button type="button" className="tsol-link" disabled={linking || linked} onClick={link}>
              <i className={`fas ${linking ? 'fa-spinner fa-spin' : linked ? 'fa-check' : 'fa-link'}`} aria-hidden="true"></i>{' '}
              {linked ? 'Linked to ticket' : linking ? 'Linking' : 'Link to ticket'}
            </button>
          </div>
          {files > 0 && (
            <div className="tsol-files">
              {row.attachments.map((file) => (
                <span className="tsol-file" key={file.file_name}>
                  {file.id > 0 && (
                    <span className="tsol-pick">
                      <SettingsCheck
                        className="tsol-file-pick"
                        title="Choose for the ticket"
                        aria-label={`Choose ${file.file_name} for the ticket`}
                        checked={picked.includes(file.id)}
                        onChange={(on) => setPicked((was) => (on ? [...was, file.id] : was.filter((n) => n !== file.id)))}
                      />
                    </span>
                  )}
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <i className={`fas ${file.is_image ? 'fa-image' : 'fa-paperclip'}`} aria-hidden="true"></i>
                    {file.file_name}
                  </a>
                </span>
              ))}
              {picked.length > 0 && (
                <button type="button" className="tsol-attach" disabled={attaching} onClick={attach}>
                  <i className={`fas ${attaching ? 'fa-spinner fa-spin' : 'fa-paperclip'}`} aria-hidden="true"></i>{' '}
                  {attaching ? 'Attaching' : 'Attach to ticket'} {!attaching && <span className="tsol-count">{picked.length}</span>}
                </button>
              )}
            </div>
          )}

          <Steps subcategory={row.subcategory_id} />

          {/* Notele, cerute abia cand cardul e desfacut - `.tsol-fb` din fila veche. */}
          <SolutionFeedback subcategory={row.subcategory_id} ticket={ticket} />

          {(tech !== '' || desc !== '') && (
            <div className="tsol-spec">
              {tech !== '' && (
                <div className="tsol-spec-row">
                  <span className="tsol-spec-name">Specific technology</span>
                  <span className="tsol-spec-val">{tech}</span>
                </div>
              )}
              {desc !== '' && (
                <div className="tsol-spec-row">
                  <span className="tsol-spec-name">Specific description</span>
                  <span className="tsol-spec-val">{desc}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/*
 * Cele patru trepte, cerute la desfacerea cardului. Treptele fara text nu se
 * arata deloc - aratate goale, ocupau jumatate din solutie cu titluri sub care
 * nu era nimic.
 */
function Steps({ subcategory }: { subcategory: number }) {
  const asks = useQueries({ queries: LEVELS.map((level) => levelDescriptionQuery(subcategory, level)) })

  if (asks.some((q) => q.isPending)) return <div className="tsol-wait">Loading…</div>

  const steps = LEVELS.map((level, i) => ({ level, data: asks[i].data })).filter(
    (s) => s.data !== undefined && s.data.description.replace(/<[^>]*>/g, '').trim() !== ''
  )

  if (steps.length === 0) {
    return <div className="tsol-step-text tsol-step-none">Nothing written under this solution yet.</div>
  }

  return (
    <>
      {steps.map((s) => (
        <div className="tsol-step" key={s.level}>
          <div className="tsol-step-name" style={{ color: LEVEL_COLORS[s.level] }}>
            <span className="tsol-dot" style={{ background: LEVEL_COLORS[s.level] }}></span>
            {s.data?.title || `Level ${s.level}`}
          </div>
          {/* Textul treptei vine dintr-un camp de text bogat, scris de cine
              administreaza solutiile - se arata cum e scris, ca in portal si
              pe ecranul mare. */}
          <div className="tsol-step-text" dangerouslySetInnerHTML={{ __html: s.data?.description ?? '' }} />
        </div>
      ))}
    </>
  )
}
